import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Search,
  Filter,
  CalendarIcon,
  Play,
  ChevronLeft,
  ChevronRight,
  Phone,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";

const CallsPage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    agent: "",
    campaign: "",
    is_analyzed: "",
  });
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [wordCloud, setWordCloud] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "25",
        date_from: format(dateRange.from, "yyyy-MM-dd 00:00:00"),
        date_to: format(dateRange.to, "yyyy-MM-dd 23:59:59"),
      });

      if (search) params.append("search", search);
      if (filters.agent) params.append("agent", filters.agent);
      if (filters.campaign) params.append("campaign", filters.campaign);
      if (filters.is_analyzed !== "") params.append("is_analyzed", filters.is_analyzed);

      const response = await authAxios.get(`/calls?${params}`);
      setCalls(response.data.calls);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching calls:", error);
      toast.error("Error al cargar llamadas");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [agentsRes, campaignsRes, wordCloudRes] = await Promise.all([
        authAxios.get("/agents"),
        authAxios.get("/campaigns"),
        authAxios.get("/dashboard/word-cloud"),
      ]);
      setAgents(agentsRes.data);
      setCampaigns(campaignsRes.data);
      setWordCloud(wordCloudRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchCalls();
  }, [page, dateRange, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCalls();
  };

  const handleAnalyze = async (callId, e) => {
    e.stopPropagation();
    setAnalyzing(callId);
    try {
      await authAxios.post(`/calls/${callId}/analyze`);
      toast.success("Análisis iniciado");
      setTimeout(fetchCalls, 2000);
    } catch (error) {
      toast.error("Error al iniciar análisis");
    } finally {
      setAnalyzing(null);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getSentimentColor = (sentiment) => {
    if (!sentiment) return "bg-slate-100 text-slate-600";
    if (sentiment >= 7) return "bg-green-100 text-green-700";
    if (sentiment >= 4) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const dateFrom = format(dateRange.from, "yyyy-MM-dd 00:00:00");
      const dateTo = format(dateRange.to, "yyyy-MM-dd 23:59:59");
      await authAxios.post(`/ucontact/sync-records?date_from=${dateFrom}&date_to=${dateTo}`);
      toast.success("Sincronización iniciada correctamente");
      setTimeout(fetchCalls, 3000);
    } catch (error) {
      toast.error("Error al iniciar sincronización");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Llamadas</h1>
          <p className="text-slate-500 mt-1">
            {total.toLocaleString()} llamadas encontradas
          </p>
        </div>
        <Button 
          onClick={handleSync} 
          disabled={syncing}
          className="bg-green-600 hover:bg-green-700"
        >
          {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          Sincronizar Hoy (22/05)
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-sky-50 border-sky-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-sky-700">{total}</p>
              <p className="text-sm text-sky-600">Llamadas Totales</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">
                {calls.filter(c => c.is_analyzed).length}
              </p>
              <p className="text-sm text-green-600">Analizadas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-700">
                {calls.length > 0 ? formatDuration(Math.round(calls.reduce((a, c) => a + c.duration, 0) / calls.length)) : "0:00"}
              </p>
              <p className="text-sm text-orange-600">Duración Promedio</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-700">{agents.length}</p>
              <p className="text-sm text-slate-600">Agentes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Word Cloud */}
      <Card data-testid="calls-word-cloud">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Temas Detectados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-lg min-h-[100px]">
            {(wordCloud?.general || wordCloud || []).slice(0, 30).map((word, i) => {
              const size = Math.max(12, Math.min(24, 12 + word.count));
              const colors = ['text-sky-600', 'text-orange-500', 'text-green-600', 'text-purple-600', 'text-slate-600'];
              return (
                <span
                  key={word.word}
                  className={`${colors[i % colors.length]} font-medium cursor-pointer hover:opacity-70 transition-opacity`}
                  style={{ fontSize: `${size}px` }}
                  onClick={() => setSearch(word.word)}
                >
                  {word.word}
                </span>
              );
            })}
            {(!wordCloud?.general?.length && !wordCloud?.length) && (
              <p className="text-slate-400 w-full text-center">No hay datos suficientes para mostrar temas</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por teléfono, agente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="calls-search-input"
                />
              </div>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2" data-testid="calls-date-filter">
                  <CalendarIcon className="w-4 h-4" />
                  {format(dateRange.from, "dd/MM", { locale: es })} - {format(dateRange.to, "dd/MM", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => range && setDateRange(range)}
                  numberOfMonths={2}
                  locale={es}
                />
              </PopoverContent>
            </Popover>

            <Select
              value={filters.agent || "all"}
              onValueChange={(v) => setFilters({ ...filters, agent: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-[180px]" data-testid="calls-agent-filter">
                <SelectValue placeholder="Todos los agentes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los agentes</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent} value={agent}>{agent}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.is_analyzed || "all"}
              onValueChange={(v) => setFilters({ ...filters, is_analyzed: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-[150px]" data-testid="calls-analyzed-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="true">Analizadas</SelectItem>
                <SelectItem value="false">Sin analizar</SelectItem>
              </SelectContent>
            </Select>

            <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="calls-search-btn">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card data-testid="calls-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Fecha y Hora</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Duración</TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Campaña</TableHead>
                    <TableHead>Sentimiento</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calls.map((call) => (
                    <TableRow
                      key={call.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/calls/${call.id}`)}
                      data-testid={`call-row-${call.id}`}
                    >
                      <TableCell className="font-mono text-sm">{call.ucontact_id || call.id.slice(0, 8)}</TableCell>
                      <TableCell>{call.call_date ? format(new Date(call.call_date), "dd/MM/yyyy HH:mm", { locale: es }) : "-"}</TableCell>
                      <TableCell>{call.source || "-"}</TableCell>
                      <TableCell>{call.destination || "-"}</TableCell>
                      <TableCell>{formatDuration(call.duration)}</TableCell>
                      <TableCell>{call.agent || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {call.campaign || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSentimentColor(call.sentiment_analysis?.general_sentiment)}>
                          {call.sentiment_analysis?.general_sentiment?.toFixed(1) || "N/A"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {call.is_analyzed ? (
                          <Badge className="bg-green-100 text-green-700">Analizada</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">Pendiente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!call.is_analyzed && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleAnalyze(call.id, e)}
                              disabled={analyzing === call.id}
                              data-testid={`analyze-btn-${call.id}`}
                            >
                              {analyzing === call.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                "Analizar"
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="bg-sky-600 hover:bg-sky-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/calls/${call.id}`);
                            }}
                            data-testid={`view-btn-${call.id}`}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {calls.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        No se encontraron llamadas
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Mostrando {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CallsPage;
